import React, { useCallback, useMemo, useState } from 'react'
import { Pressable, ScrollView, StyleSheet, View } from 'react-native'
import { useConfirmDialog } from '../../components/ConfirmDialog'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { useQuery, useMutation } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import type { Id } from '@ppwr/convex/_generated/dataModel'
import { DS } from '@/design-system'
import { useStore } from '@/models'
import { hasOwnerRights } from '@ppwr/convex/lounges/shared'
import { translate } from '../../i18n'
import { SettingsScreenLayout, SettingsLoadingView } from '@/components/layouts/SettingsScreenLayout'
import { LoungeCodeCard } from './components/LoungeCodeCard'
import { useSaveLoungeJoinCode } from './components/useSaveLoungeJoinCode'
import { LoungeAvatarPicker, LoungeTableColorPicker } from './components/LoungeCustomizationPickers'

export function LoungeSettingsScreen() {
  const { loungeId: loungeIdStr } = useLocalSearchParams<{ loungeId: string }>()
  const loungeId = loungeIdStr as Id<'lounges'> | undefined
  const router = useRouter()
  const {
    session: { user }
  } = useStore()
  const userId = user?.auth0Id ?? ''

  const lobby = useQuery(api.lounges.queries.getLoungeLobby, loungeId ? { loungeId, userId } : 'skip')

  const rotateCode = useMutation(api.lounges.mutations.rotateLoungeJoinCode)

  const [rotating, setRotating] = useState(false)
  const { showConfirm, dialogPortal } = useConfirmDialog()

  const callerMember = lobby?.members.find((m) => m.userId === userId)
  const role = callerMember?.role
  const isOwnerOrCoowner = useMemo(() => role && hasOwnerRights(role), [role])

  const joinCode = lobby?.joinCode ?? null

  const handleRotateCode = useCallback(() => {
    if (!loungeId) return
    showConfirm({
      title: translate('loungeSettingsScreen.rotateAlert.title'),
      message: translate('loungeSettingsScreen.rotateAlert.message'),
      cancelText: translate('loungeSettingsScreen.rotateAlert.cancel'),
      confirmText: translate('loungeSettingsScreen.rotateAlert.confirm'),
      onConfirm: async () => {
        setRotating(true)
        try {
          await rotateCode({ loungeId: loungeId as Id<'lounges'>, actorId: userId })
        } finally {
          setRotating(false)
        }
      }
    })
  }, [loungeId, userId, rotateCode, showConfirm])

  const handleSaveCode = useSaveLoungeJoinCode(loungeId, userId)

  if (!lobby) {
    return (
      <SettingsScreenLayout title={translate('loungeSettingsScreen.title')} onBack={() => router.back()}>
        {dialogPortal}
        <SettingsLoadingView />
      </SettingsScreenLayout>
    )
  }

  return (
    <SettingsScreenLayout title={translate('loungeSettingsScreen.title')} onBack={() => router.back()}>
      {dialogPortal}
      <ScrollView contentContainerStyle={styles.content}>
        {/* Join Code Card */}
        <LoungeCodeCard
          mode="admin"
          joinCode={joinCode}
          onRotateCode={handleRotateCode}
          canRotate={!!isOwnerOrCoowner}
          rotating={rotating}
          loungeId={loungeId}
          onSaveCode={isOwnerOrCoowner ? handleSaveCode : undefined}
        />

        {/* Sub-section navigation */}
        <View style={styles.navSection}>
          <Pressable
            onPress={() =>
              router.push({
                pathname: '/lounges/[loungeId]/settings/branding',
                params: { loungeId: loungeId as string }
              } as any)
            }
            style={styles.navRow}
            accessibilityRole="button"
            accessibilityLabel={translate('loungeSettingsScreen.nav.brandingAndIdentityAccessibilityLabel')}
          >
            <View style={[styles.navIcon, styles.navIconOrange]}>
              <DS.Icon name="Branding_Icon" size={32} color="Global/White" />
            </View>
            <DS.Text variant="Heading 3/Medium" style={styles.navTitle}>
              {translate('loungeSettingsScreen.nav.brandingAndIdentity')}
            </DS.Text>
            <DS.Icon name="Arrow---right" color="Brand/Dark Grey" size={20} />
          </Pressable>

          <Pressable
            onPress={() => {
              if (!loungeId) return
              router.push({
                pathname: '/lounges/[loungeId]/settings/configuration',
                params: { loungeId }
              } as any)
            }}
            style={styles.navRow}
            accessibilityRole="button"
            accessibilityLabel={translate('loungeSettingsScreen.nav.configuration')}
          >
            <View style={[styles.navIcon, styles.navIconGrey]}>
              <DS.Icon name="Settings_Gear" size={32} color="Global/White" />
            </View>
            <DS.Text variant="Heading 3/Medium" style={styles.navTitle}>
              {translate('loungeSettingsScreen.nav.configuration')}
            </DS.Text>
            <DS.Icon name="Arrow---right" color="Brand/Dark Grey" size={20} />
          </Pressable>

          <Pressable
            onPress={() =>
              router.push({
                pathname: '/lounges/[loungeId]/settings/scheduled-games',
                params: { loungeId: loungeId as string }
              } as any)
            }
            style={styles.navRow}
            accessibilityRole="button"
            accessibilityLabel={translate('loungeSettingsScreen.nav.scheduledGames')}
          >
            <View style={[styles.navIcon, styles.navIconMaroon]}>
              <DS.Icon name="IconsMenu" size={34} color="Global/White" />
            </View>
            <DS.Text variant="Heading 3/Medium" style={styles.navTitle}>
              {translate('loungeSettingsScreen.nav.scheduledGames')}
            </DS.Text>
            <DS.Icon name="Arrow---right" color="Brand/Dark Grey" size={20} />
          </Pressable>
        </View>
      </ScrollView>
    </SettingsScreenLayout>
  )
}

const styles = StyleSheet.create({
  content: {
    padding: 16,
    gap: 24
  },
  navSection: {
    gap: 12
  },
  navRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    backgroundColor: DS.palette['Global/White'],
    borderRadius: 999,
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderWidth: 1,
    borderColor: DS.palette['Brand/Light Grey']
  },
  navIcon: {
    width: 40,
    height: 40,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center'
  },
  navIconOrange: {
    backgroundColor: DS.palette['Brand/Orange']
  },
  navIconGrey: {
    backgroundColor: DS.palette['Brand/Dark Grey']
  },
  navIconMaroon: {
    backgroundColor: DS.palette['Brand/Maroon']
  },
  navTitle: {
    flex: 1
  }
})
